<template>
    <div>
        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/FinancialServices/IDTs">
              Financial Services
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/FinancialServices/Leases">Computer Leases</b-breadcrumb-item>
            <b-breadcrumb-item active>Add</b-breadcrumb-item>
        </b-breadcrumb>

        <form @submit.prevent="onSubmit()">
            <div class="card">
                <div class="bg-dark card-header text-white">Create New Computer Lease</div>
                <div class="card-body">
                    <AppCardCollapsable :display-footer="false" :is-expanded="selectComputerAssetExpanded" class="mb-3">
                        <template v-slot:header>Select Computer</template>
                        <template v-slot:body>
                            <div class="row">
                                <b-form-group class="col-lg-6 col-md-12" label="Search Computer Assets By" v-slot="{ searchByValue }">
                                    <b-form-radio-group
                                      v-model="searchBy"
                                      :options="searchByOptions"
                                      :aria-describedby="searchByValue"
                                      :disabled="isSearching"
                                    ></b-form-radio-group>
                                </b-form-group>
                            </div>
                            <div class="row">
                                <div class="col-lg-3 col-md-4 form-group">
                                    <input  class="form-control"
                                            maxlength="50"
                                            :disabled="isSearching"
                                            v-model="searchKeyword" />
                                </div>
                            </div>
                            <div class="mb-3 text-left">
                                <button @click.prevent="toggleSearchFields()" class="btn btn-primary d-print-none">
                                  Search
                                </button>
                            </div>
                            <BTable :busy="isSearching"
                                    :fields="possibleComputerAssetsTableFields"
                                    :items="possibleComputerAssets"
                                    :responsive="true"
                                    primary-key="id"
                                    show-empty
                                    striped
                                    table-class="table-align-middle">

                                <template v-slot:cell(id)="row">
                                    <button @click.prevent="selectComputerAsset(row)" class="btn btn-success d-print-none">
                                      Select
                                    </button>
                                </template>

                                <template v-slot:custom-foot>
                                    <b-tr>
                                        <b-td :colspan="possibleComputerAssetsTableFields.length">
                                            <AppTablePagination
                                                    :page.sync="computerAssetsFilterOptions.page"
                                                    :pageSize.sync="computerAssetsFilterOptions.pageSize"
                                                    :totalCount.sync="computerAssetsTotalCount"
                                                    v-on:update:page="loadPossibleComputerAssets()"
                                                    v-on:update:pageSize="loadPossibleComputerAssets()"
                                            ></AppTablePagination>
                                        </b-td>
                                    </b-tr>
                                </template>
                            </BTable>
                        </template>
                    </AppCardCollapsable>

                    <template v-if="selectedComputerAsset !== null">
                      <b-container>
                          <b-row>
                              <b-col>
                                  <div class="font-weight-bold">Asset Number</div>
                                  <div class="pl-4">{{ selectedComputerAsset.assetNumber }}</div>
                              </b-col>
                              <b-col>
                                  <div class="font-weight-bold">Serial Number</div>
                                  <div class="pl-4">{{ selectedComputerAsset.serialNumber }}</div>
                              </b-col>
                          </b-row>
                          <b-row>
                              <b-col>
                                  <div class="font-weight-bold">Manufacturer</div>
                                  <div class="pl-4">{{ selectedComputerAsset.manufacturer }}</div>
                              </b-col>
                              <b-col>
                                  <div class="font-weight-bold">Model Number</div>
                                  <div class="pl-4">{{ selectedComputerAsset.modelNumber }}</div>
                              </b-col>
                          </b-row>
                          <b-row>
                              <b-col>
                                  <div class="font-weight-bold">Description</div>
                                  <div class="pl-4">{{ selectedComputerAsset.description }}</div>
                              </b-col>
                              <b-col>
                                  <div class="font-weight-bold">Vendor</div>
                                  <div class="pl-4">{{ selectedComputerAsset.vendor }}</div>
                              </b-col>
                          </b-row>
                          <b-row>
                              <b-col>
                                  <div class="font-weight-bold">Purchase Amount</div>
                                  <div class="pl-4">{{ selectedComputerAsset.purchaseAmount }}</div>
                              </b-col>
                              <b-col>
                                  <div class="font-weight-bold">Purchase Date</div>
                                  <div class="pl-4">{{ $moment(selectedComputerAsset.purchaseDate).format("MM/DD/YYYY") }}</div>
                              </b-col>
                          </b-row>
                          <b-row>
                              <b-col>
                                  <div class="font-weight-bold">Custodian</div>
                                  <div class="pl-4">{{ selectedComputerAsset.custodianPersonName }}</div>
                              </b-col>
                          </b-row>
                      </b-container>

                      <br />

                      <div class="row">
                          <div class="col-lg-6 col-md-12 form-group">
                              <label for="billingAccountId">Billing Account</label>
                              <select :class="{'is-invalid': $v.newComputerLease.leasePurchaseAccountId.$invalid && isValidating}"
                                      class="form-control" v-model="newComputerLease.leasePurchaseAccountId">
                                  <option :value="null" disabled>--Select Account--</option>
                                  <option :key="billingAccount.id" :value="billingAccount.id" v-for="billingAccount in billingAccounts">{{billingAccount.sl}}-{{billingAccount.sa}}</option>
                              </select>
                              <span v-if="!$v.newComputerLease.leasePurchaseAccountId.required && isValidating" class="invalid-feedback">
                                  Field is required
                              </span>
                          </div>
                      </div>

                      <div class="row">
                          <div class="col-lg-7 col-md-8 row">
                            <div class="col-6 form-group">
                                <label for="leaseStartDate">Lease Start Date</label>
                                <b-input-group class="mb-3">
                                  <b-form-input :state="$v.newComputerLease.leaseStartDate.$invalid && isValidating ? false : null"
                                    v-model="tempLeaseStartDate"
                                    type="text"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    @change="updateLeaseStartDateFromInput()"
                                  ></b-form-input>
                                  <b-input-group-append>
                                    <b-form-datepicker
                                      id="leaseStartDate"
                                      v-model="newComputerLease.leaseStartDate"
                                      button-only
                                      right
                                      locale="en-US"
                                      aria-controls="leaseStartDate"
                                      @context="updateLeaseStartDateFromButton()"
                                    ></b-form-datepicker>
                                  </b-input-group-append>
                                </b-input-group>
                                <div v-if="!$v.newComputerLease.leaseStartDate.valueCheck && isValidating" class="col-12 invalid-feedback" style="display: block;">
                                    Start date must occur on or before end date
                                </div>
                            </div>
                            <div class="col-6 form-group">
                                <label for="leaseEndDate">Lease End Date</label>
                                <b-input-group class="mb-3">
                                  <b-form-input :state="$v.newComputerLease.leaseEndDate.$invalid && isValidating ? false : null"
                                    v-model="tempLeaseEndDate"
                                    type="text"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    @change="updateLeaseEndDateFromInput()"
                                  ></b-form-input>
                                  <b-input-group-append>
                                    <b-form-datepicker
                                      id="leaseEndDate"
                                      v-model="newComputerLease.leaseEndDate"
                                      button-only
                                      right
                                      locale="en-US"
                                      aria-controls="leaseEndDate"
                                      :min="newComputerLease.leaseStartDate"
                                      @context="updateLeaseEndDateFromButton()"
                                    ></b-form-datepicker>
                                  </b-input-group-append>
                                </b-input-group>
                            </div>
                          </div>
                          <div class="col-lg-2 col-md-3 form-group">
                              <label for="leaseRate">Monthly Lease Rate</label>
                              <input class="form-control"
                                      v-model="newComputerLease.leaseRate"/>
                          </div>
                          <div class="col align-self-center">
                              <button @click.prevent="setDefaultLeaseRate()" class="btn btn-secondary d-print-none">
                                Calculate Defaults
                              </button>
                          </div>
                          <div class="col text-center">
                              <label>Months Left on Lease</label>
                              <div>{{monthsLeftOnLease}}</div>
                          </div>
                      </div>

                      <div class="row">
                          <div class="col-lg-6 col-md-12 form-group">
                              <label for="lesseePersonId">Lessee</label>
                              <select class="form-control" v-model="newComputerLease.lesseePersonId">
                                  <option :value="null" disabled>--Select Person--</option>
                                  <option :key="lessee.id" :value="lessee.id" v-for="lessee in lessees">{{lessee.name}}</option>
                              </select>
                          </div>
                      </div>

                      <div class="row">
                        <div class="col-lg-3 col-md-3 form-group">
                            <label for="warrantyStartDate">Warranty Start Date</label>
                            <b-input-group class="mb-3">
                              <b-form-input :state="$v.newComputerLease.warrantyStartDate.$invalid && isValidating ? false : null"
                                v-model="tempWarrantyStartDate"
                                type="text"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                @change="updateWarrantyStartDateFromInput()"
                              ></b-form-input>
                              <b-input-group-append>
                                <b-form-datepicker
                                  id="warrantyStartDate"
                                  v-model="newComputerLease.warrantyStartDate"
                                  button-only
                                  right
                                  locale="en-US"
                                  aria-controls="warrantyStartDate"
                                  @context="updateWarrantyStartDateFromButton()"
                                ></b-form-datepicker>
                              </b-input-group-append>
                            </b-input-group>
                            <div v-if="!$v.newComputerLease.warrantyStartDate.valueCheck && isValidating" class="col-12 invalid-feedback" style="display: block;">
                              Start date must occur on or before end date
                            </div>
                        </div>
                        <div class="col-lg-3 col-md-3 form-group">
                            <label for="warrantyEndDate">Warranty End Date</label>
                            <b-input-group class="mb-3">
                              <b-form-input :state="$v.newComputerLease.warrantyEndDate.$invalid && isValidating ? false : null"
                                v-model="tempWarrantyEndDate"
                                type="text"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                @change="updateWarrantyEndDateFromInput()"
                              ></b-form-input>
                              <b-input-group-append>
                                <b-form-datepicker
                                  id="warrantyEndDate"
                                  v-model="newComputerLease.warrantyEndDate"
                                  button-only
                                  right
                                  locale="en-US"
                                  aria-controls="warrantyEndDate"
                                  :min="newComputerLease.warrantyStartDate"
                                  @context="updateWarrantyEndDateFromButton()"
                                ></b-form-datepicker>
                              </b-input-group-append>
                            </b-input-group>
                        </div>
                      </div>
                      
                      <div class="row">
                          <div class="col form-group">
                              <label for="notes">Notes</label>
                              <b-form-textarea 
                                size="sm"
                                v-model="newComputerLease.notes"
                              ></b-form-textarea>
                          </div>
                      </div>
                    </template>

                </div>
                <div class="card-footer d-print-none text-right">
                    <button :disabled="isSubmitting || selectedComputerAsset == null" class="btn btn-primary" type="submit">Save</button>
                    <RouterLink :to="{name:'ComputerLeases'}" class="btn btn-danger">Back</RouterLink>
                </div>
            </div>
        </form>

    </div>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import {required, requiredIf} from "vuelidate/lib/validators";
    import AppCardCollapsable from "@/components/shared/AppCardCollapsable.vue";
    import ComputersService from "@/services/ComputersService";
    import AccountsService from "@/services/AccountsService";
    import PersonService from "@/services/PersonService";

    export default {
        components: {
          AppCardCollapsable,
        },
        data() {
            return {
                isSearching: false,
                selectComputerAssetExpanded: true,
                searchKeyword: null,
                searchBy: 0,
                searchByOptions: [
                    {
                        value: 0,
                        text: 'Asset Number'
                    },
                    {
                        value: 1,
                        text: 'Serial Number'
                    }
                ],
                computerAssetsFilterOptions: {
                  page: 1,
                  pageSize: 10,
                  returnAll: false,
                  assetNumber: null,
                  serialNumber: null,
                  manufacturer: null,
                  modelNumber: null,
                  description: null,
                  vendor: null,
                  existsInFamis: null,
                  excludeWithComputerLease: true
                },
                computerAssetsTotalCount: 0,
                possibleComputerAssets: [],
                possibleComputerAssetsTableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    }, 
                    {
                        key: 'assetNumber',
                        label: 'Asset Number'
                    },
                    {
                        key: 'serialNumber',
                        label: 'Serial Number'
                    },
                    {
                        key: 'description',
                        label: 'Description',
                    },
                    {
                        key: 'manufacturer',
                        label: 'Manufacturer',
                    },
                    {
                        key: 'modelNumber',
                        label: 'Model Number',
                    }
                ],
                billingAccounts: [],
                lessees: [],
                isSubmitting: false,
                isValidating: false,
                selectedComputerAsset: null,
                newComputerLease: {
                  assetId: null,
                  leasePurchaseAccountId: null,
                  leaseRate: null,
                  leaseStartDate: null,
                  leaseEndDate: null,
                  lesseePersonId: null,
                  warrantyStartDate: null,
                  warrantyEndDate: null,
                  notes: null
                },
                tempLeaseStartDate: null,
                tempLeaseEndDate: null,
                tempWarrantyStartDate: null,
                tempWarrantyEndDate: null
            }
        },
        watch: {
            'newComputerLease.warrantyStartDate' () {
                if (this.newComputerLease.warrantyStartDate !== null) {
                    this.newComputerLease.warrantyEndDate = this.$moment(this.newComputerLease.warrantyStartDate).add(3, 'y').toDate();
                }
            }
        },
        computed: {
            monthsLeftOnLease () {
                if (this.newComputerLease.leaseStartDate !== null 
                    && this.newComputerLease.leaseEndDate !== null) {
                    const startDate = new Date();
                    const endDate = new Date(this.newComputerLease.leaseEndDate);
                    startDate.setDate(1);
                    let retVal = endDate.getMonth() - startDate.getMonth() + 12 * (endDate.getFullYear() - startDate.getFullYear());

                    if (endDate.getDate() !== 1) {
                      retVal += 1;
                    }


                    return retVal;
                }

                return 0;
            }
        },
        methods: {
            /**
             * @description Creates a computer lease and redirects if successful
             */
            onSubmit() {
                const vm = this;
                vm.isValidating = true;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;

                ComputersService.addComputerLease(this.newComputerLease)
                    .then(response => {
                      vm.alert(ToastType.Success, "The computer lease was successfully created");
                      vm.$router.replace({name:'ComputerLeases'})
                    })
                    .catch((error) => {
                      vm.alert(ToastType.Danger, error.response.data)
                    })
                    .finally(() => {
                      vm.isValidating = false;
                      vm.isSubmitting = false;
                    })
            },

            loadPossibleComputerAssets () {
                const vm = this;
                this.isSearching = true;

                ComputersService.getComputerAssets(this.computerAssetsFilterOptions)
                  .then(response => {
                    vm.possibleComputerAssets = response.data;
                    vm.computerAssetsTotalCount = response.totalCount;
                  })
                  .catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)
                  })
                  .finally(() => {
                    this.isSearching = false;
                  })
            },

            loadAccounts () {
              const vm = this;

              AccountsService.getAccounts({
                returnAll: true,
                page: 1,
                pageSize: 10,
                isDeleted: false,
                isEnded: false,
                isFrozen: false
              })
                  .then(response => {
                      this.billingAccounts = response.data;
                  })
                  .catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)
                  })
            },

            loadLessees () {
              const vm = this;

              PersonService.getPeopleList({
                page: 1,
                pageSize: 10,
                name: null,
                uin: null,
                organizationCode: null,
                onlyShowActive: true,
                onlyShowTTI: true,
                onlyShowMissingAddress: false,
                returnAll: true
              })
                  .then(response => {
                      this.lessees = response.data;
                  })
                  .catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)
                  })
            },

            toggleSearchFields () {
              this.computerAssetsFilterOptions.page = 1;
              this.computerAssetsFilterOptions.pageSize = 10;
              if (this.searchBy === 0) {
                this.computerAssetsFilterOptions.assetNumber = this.searchKeyword;
                this.computerAssetsFilterOptions.serialNumber = null;
              } else if (this.searchBy === 1) {
                this.computerAssetsFilterOptions.assetNumber = null;
                this.computerAssetsFilterOptions.serialNumber = this.searchKeyword;
              } else {
                this.computerAssetsFilterOptions.assetNumber = null;
                this.computerAssetsFilterOptions.serialNumber = null;
              }
              this.loadPossibleComputerAssets();
            },

            selectComputerAsset (row) {
              this.selectedComputerAsset = {
                id: row.item.id,
                assetNumber: row.item.assetNumber,
                serialNumber: row.item.serialNumber,
                manufacturer: row.item.manufacturer,
                modelNumber: row.item.modelNumber,
                description: row.item.description,
                vendor: row.item.vendor,
                custodianPersonName: row.item.custodianPersonName,
                purchaseAmount: row.item.purchaseAmount,
                purchaseDate: row.item.purchaseDate
              }

              this.newComputerLease = {
                assetId: row.item.id,
                leasePurchaseAccountId: null,
                leaseRate: null,
                leaseStartDate: null,
                leaseEndDate: null,
                lesseePersonId: null,
                warrantyEndDate: null,
                warrantyStartDate: null,
                notes: null
              }
              this.selectComputerAssetExpanded = false
            },

            setDefaultLeaseRate () {
                if (this.monthsLeftOnLease > 0) {
                    this.newComputerLease.leaseRate = Math.round((Number(this.selectedComputerAsset.purchaseAmount / this.monthsLeftOnLease) + Number.EPSILON) * 100) / 100
                } else {
                    this.newComputerLease.leaseRate = 0
                }
            },

            updateLeaseStartDateFromInput () {
                if (this.tempLeaseStartDate !== null) {
                  this.newComputerLease.leaseStartDate = this.$moment(this.tempLeaseStartDate, "MM/DD/YYYY").toDate()
                }
            },

            updateLeaseStartDateFromButton () {
                if (this.newComputerLease.leaseStartDate !== null) {
                  this.tempLeaseStartDate = this.$moment(this.newComputerLease.leaseStartDate).format("MM/DD/YYYY")
                }
            },

            updateLeaseEndDateFromInput () {
                if (this.tempLeaseEndDate !== null) {
                  this.newComputerLease.leaseEndDate = this.$moment(this.tempLeaseEndDate, "MM/DD/YYYY").toDate()
                }
            },

            updateLeaseEndDateFromButton () {
                if (this.newComputerLease.leaseEndDate !== null) {
                  this.tempLeaseEndDate = this.$moment(this.newComputerLease.leaseEndDate).format("MM/DD/YYYY")
                }
            },
            
            updateWarrantyStartDateFromInput () {
                if (this.tempWarrantyStartDate !== null) {
                  this.newComputerLease.warrantyStartDate = this.$moment(this.tempWarrantyStartDate, "MM/DD/YYYY").toDate()
                }
            },

            updateWarrantyStartDateFromButton () {
                if (this.newComputerLease.warrantyStartDate !== null) {
                  this.tempWarrantyStartDate = this.$moment(this.newComputerLease.warrantyStartDate).format("MM/DD/YYYY")
                }
            },

            updateWarrantyEndDateFromInput () {
                if (this.tempWarrantyEndDate !== null) {
                  this.newComputerLease.warrantyEndDate = this.$moment(this.tempWarrantyEndDate, "MM/DD/YYYY").toDate()
                }
            },

            updateWarrantyEndDateFromButton () {
                if (this.newComputerLease.warrantyEndDate !== null) {
                  this.tempWarrantyEndDate = this.$moment(this.newComputerLease.warrantyEndDate).format("MM/DD/YYYY")
                }
            }
        },
        validations: {
            newComputerLease: {
              assetId: {
                required
              },
              leasePurchaseAccountId: {
                required
              },
              leaseStartDate: {
                required: requiredIf(function (value) {
                  return value.leaseStartDate !== null
                }),
                valueCheck() {
                  if (this.newComputerLease.leaseStartDate !== null && this.newComputerLease.leaseEndDate !== null) {
                    if (this.newComputerLease.leaseStartDate === this.newComputerLease.leaseEndDate) {
                      return false
                    }
                    return (new Date(this.newComputerLease.leaseEndDate)).getFullYear() > (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() > (new Date(this.newComputerLease.leaseStartDate)).getMonth())
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() === (new Date(this.newComputerLease.leaseStartDate)).getMonth()
                          && (new Date(this.newComputerLease.leaseEndDate)).getDate() > (new Date(this.newComputerLease.leaseStartDate)).getDate())
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() === (new Date(this.newComputerLease.leaseStartDate)).getMonth()
                          && (new Date(this.newComputerLease.leaseEndDate)).getDate() === (new Date(this.newComputerLease.leaseStartDate)).getDate())
                  }
                  return true
                }
              },
              leaseEndDate: {
                required: requiredIf(function (value) {
                  return value.leaseEndDate !== null
                }),
                valueCheck() {
                  if (this.newComputerLease.leaseStartDate !== null && this.newComputerLease.leaseEndDate !== null) {
                    if (this.newComputerLease.leaseStartDate === this.newComputerLease.leaseEndDate) {
                      return false
                    }
                    return (new Date(this.newComputerLease.leaseEndDate)).getFullYear() > (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() > (new Date(this.newComputerLease.leaseStartDate)).getMonth())
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() === (new Date(this.newComputerLease.leaseStartDate)).getMonth()
                          && (new Date(this.newComputerLease.leaseEndDate)).getDate() > (new Date(this.newComputerLease.leaseStartDate)).getDate())
                      || ((new Date(this.newComputerLease.leaseEndDate)).getFullYear() === (new Date(this.newComputerLease.leaseStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.leaseEndDate)).getMonth() === (new Date(this.newComputerLease.leaseStartDate)).getMonth()
                          && (new Date(this.newComputerLease.leaseEndDate)).getDate() === (new Date(this.newComputerLease.leaseStartDate)).getDate())
                  }
                  return true
                }
              },
              warrantyStartDate: {
                required: requiredIf(function (value) {
                  return value.warrantyStartDate !== null
                }),
                valueCheck() {
                  if (this.newComputerLease.warrantyStartDate !== null && this.newComputerLease.warrantyEndDate !== null) {
                    return (new Date(this.newComputerLease.warrantyEndDate)).getFullYear() > (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() > (new Date(this.newComputerLease.warrantyStartDate)).getMonth())
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() === (new Date(this.newComputerLease.warrantyStartDate)).getMonth()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getDate() > (new Date(this.newComputerLease.warrantyStartDate)).getDate())
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() === (new Date(this.newComputerLease.warrantyStartDate)).getMonth()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getDate() === (new Date(this.newComputerLease.warrantyStartDate)).getDate())
                  }
                  return true
                }
              },
              warrantyEndDate: {
                required: requiredIf(function (value) {
                  return value.warrantyEndDate !== null
                }),
                valueCheck() {
                  if (this.newComputerLease.warrantyStartDate !== null && this.newComputerLease.warrantyEndDate !== null) {
                    return (new Date(this.newComputerLease.warrantyEndDate)).getFullYear() > (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() > (new Date(this.newComputerLease.warrantyStartDate)).getMonth())
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() === (new Date(this.newComputerLease.warrantyStartDate)).getMonth()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getDate() > (new Date(this.newComputerLease.warrantyStartDate)).getDate())
                      || ((new Date(this.newComputerLease.warrantyEndDate)).getFullYear() === (new Date(this.newComputerLease.warrantyStartDate)).getFullYear()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getMonth() === (new Date(this.newComputerLease.warrantyStartDate)).getMonth()
                          && (new Date(this.newComputerLease.warrantyEndDate)).getDate() === (new Date(this.newComputerLease.warrantyStartDate)).getDate())
                  }
                  return true
                }
              }
            }
        },
        created () {
          this.loadAccounts()
          this.loadLessees()
        }
    }
</script>
